/**
 * Cloud Sync Page - Continuous additive sync of photos to the user's own cloud
 * (Google Drive; Dropbox/OneDrive in future phases).
 *
 * Sections: provider connection, project/view selection (ShareDialog-style
 * lists), sync progress, destination quota. State is read live from Firestore
 * (cloudSync/{uid}); actions go through the export-worker /sync/* endpoints.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Divider,
  Empty,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import AppLayout from "@/components/AppLayout";
import { MaterialIcon } from "@/components/MaterialIcon";
import { SectionLabel } from "@/components/SectionLabel";
import { BORDER, RADIUS, SECONDARY } from "@/styles/design-tokens";
import { useAuthStore } from "@/store/authStore";
import { useCloudAccounts } from "@/hooks/useCloudAccounts";
import { useCloudSync } from "@/hooks/useCloudSync";
import {
  ENABLED_PROVIDERS,
  PROVIDER_LABELS,
  disconnectAccount,
  getOAuthStartUrl,
  type CloudAccount,
  type CloudProviderId,
} from "@/services/cloudAccountsService";
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "@/config/firebase";
import {
  disableSync,
  enableSync,
  pauseSync,
  resumeSync,
  updateSyncSelection,
  type SyncSelection,
} from "@/services/syncService";
import { getProjects } from "@/services/projectService";
import { getViewsByProject } from "@/services/viewService";
import type { Project, View } from "@/types";

const { Title, Text } = Typography;

interface ProjectWithViews {
  project: Project;
  views: View[];
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function viewBytes(view: View): number {
  return view.stats?.storageBytes ?? (view.stats?.storageMB ?? 0) * 1024 * 1024;
}

const viewKey = (projectId: string, viewId: string) => `${projectId}|${viewId}`;

/** Card section title — bigger than the antd small-card default. */
const cardTitle = (label: string, icon?: React.ReactNode) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 17,
      fontWeight: 600,
    }}
  >
    {icon}
    {label}
  </span>
);

/**
 * Provider brand logo. Full color = sync configured on it (active/paused);
 * grayscale = account not connected or sync disabled.
 */
function ProviderLogo({
  provider,
  size = 18,
  muted = false,
}: {
  provider: CloudProviderId;
  size?: number;
  muted?: boolean;
}) {
  const style: React.CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    filter: muted ? "grayscale(1) opacity(0.45)" : undefined,
  };
  if (provider === "google") {
    return (
      <svg viewBox="0 0 87.3 78" style={style} aria-hidden="true">
        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
        <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
        <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
        <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
        <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
        <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
      </svg>
    );
  }
  if (provider === "dropbox") {
    // Dropbox glyph: four diamonds + base, brand blue
    return (
      <svg viewBox="0 0 40 34" style={style} aria-hidden="true">
        <g fill="#0061ff">
          <polygon points="10,0 20,6 10,12 0,6" />
          <polygon points="30,0 40,6 30,12 20,6" />
          <polygon points="10,12 20,18 10,24 0,18" />
          <polygon points="30,12 40,18 30,24 20,18" />
          <polygon points="20,20 30,26 20,32 10,26" />
        </g>
      </svg>
    );
  }
  // OneDrive: neutral placeholder until the provider ships
  return <MaterialIcon name="cloud" size={size} style={{ color: muted ? "#bfbfbf" : "#1677ff" }} />;
}

/** Same color ramp as the sidebar StorageBar: green, then to red near full. */
const storageBarColor = (percentage: number): string => {
  if (percentage <= 70) return "#52c41a";
  if (percentage >= 95) return "#ff4d4f";
  const ratio = (percentage - 70) / 25;
  const r = Math.round(82 + (255 - 82) * ratio);
  const g = Math.round(196 + (77 - 196) * ratio);
  const b = Math.round(26 + (79 - 26) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
};

/** Compact storage bar, same color ramp/format as the sidebar StorageBar. */
function MiniStorageBar({ quota }: { quota: NonNullable<CloudAccount["quota"]> }) {
  if (quota.totalBytes === null) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {formatBytes(quota.usedBytes)} used · unlimited storage
      </Text>
    );
  }
  const percent = Math.min(
    100,
    Math.round((quota.usedBytes / Math.max(1, quota.totalBytes)) * 100)
  );
  const color = storageBarColor(percent);
  return (
    <div style={{ maxWidth: 380 }}>
      <div style={{ height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
        <div
          style={{ width: `${percent}%`, height: "100%", background: color, borderRadius: 3 }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color }}>{percent}%</span>
        <span style={{ fontSize: 12, color: SECONDARY }}>
          {formatBytes(quota.usedBytes)} / {formatBytes(quota.totalBytes)}
        </span>
      </div>
    </div>
  );
}

const PAUSE_REASON_LABELS: Record<string, string> = {
  user: "Paused by you",
  quota_full: "Cloud storage full",
  access_revoked: "Account access revoked — reconnect to resume",
  provider_error: "Provider error",
};

export default function CloudSyncPage() {
  const { user } = useAuthStore();
  const { accounts, loading: accountsLoading } = useCloudAccounts();
  const { config, loading: configLoading } = useCloudSync();
  const { message, modal } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const [projectsData, setProjectsData] = useState<ProjectWithViews[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  // Selection = set of "projectId|viewId" keys; "all views of a project
  // selected" is stored as selection "all" (future views auto-included, D12)
  const [checkedViews, setCheckedViews] = useState<Set<string>>(new Set());
  const [selectionDirty, setSelectionDirty] = useState(false);
  // One busy key per action: only the pressed button shows its spinner
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // OAuth callback feedback (?connected= / ?connect_error=)
  useEffect(() => {
    const connected = searchParams.get("connected");
    const connectError = searchParams.get("connect_error");
    if (!connected && !connectError) return;
    if (connected) {
      message.success(`${PROVIDER_LABELS[connected as CloudProviderId] ?? connected} connected!`);
    } else if (connectError === "missing_permissions") {
      message.error(
        "Access to your files was not granted: on the consent screen you must tick the checkbox that lets MasterLapse manage the files it creates. Please connect again and allow it.",
        10
      );
    } else if (connectError === "denied") {
      message.warning("Connection cancelled.");
    } else {
      message.error(`Connection failed (${connectError})`);
    }
    searchParams.delete("connected");
    searchParams.delete("connect_error");
    searchParams.delete("provider");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, message]);

  // Load the project/view tree once
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const projects = await getProjects(user.uid);
        const withViews = await Promise.all(
          projects.map(async (project) => ({
            project,
            views: await getViewsByProject(user.uid, project.projectId),
          }))
        );
        if (!cancelled) setProjectsData(withViews);
      } catch (error) {
        console.error("Failed to load projects for sync page:", error);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Mirror the stored selection into the checkboxes until the user edits it
  useEffect(() => {
    if (selectionDirty || !config || projectsData.length === 0) return;
    const keys = new Set<string>();
    for (const { project, views } of projectsData) {
      const value = config.selection?.[project.projectId];
      if (value === "all") {
        views.forEach((v) => keys.add(viewKey(project.projectId, v.viewId)));
      } else if (Array.isArray(value)) {
        value.forEach((viewId) => keys.add(viewKey(project.projectId, viewId)));
      }
    }
    setCheckedViews(keys);
  }, [config, projectsData, selectionDirty]);

  const toggleView = (projectId: string, viewId: string) => {
    setCheckedViews((prev) => {
      const next = new Set(prev);
      const key = viewKey(projectId, viewId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSelectionDirty(true);
  };

  /** "Select all" / "Deselect all" link per project (same as the gallery). */
  const toggleProject = (projectId: string, views: View[], checked: boolean) => {
    setCheckedViews((prev) => {
      const next = new Set(prev);
      views.forEach((v) => {
        const key = viewKey(projectId, v.viewId);
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
    setSelectionDirty(true);
  };

  // Checkbox sets -> selection object. A fully-checked project becomes "all"
  // (future views auto-included, decision D12).
  const buildSelection = useCallback((): SyncSelection => {
    const selection: SyncSelection = {};
    for (const { project, views } of projectsData) {
      const selected = views.filter((v) => checkedViews.has(viewKey(project.projectId, v.viewId)));
      if (selected.length === 0) continue;
      selection[project.projectId] =
        selected.length === views.length ? "all" : selected.map((v) => v.viewId);
    }
    return selection;
  }, [checkedViews, projectsData]);

  const selectedTotals = useMemo(() => {
    let photos = 0;
    let bytes = 0;
    for (const { project, views } of projectsData) {
      for (const view of views) {
        if (checkedViews.has(viewKey(project.projectId, view.viewId))) {
          photos += view.stats?.photoCount ?? 0;
          bytes += viewBytes(view);
        }
      }
    }
    return { photos, bytes };
  }, [checkedViews, projectsData]);

  const connectedAccount = ENABLED_PROVIDERS.map((p) => accounts.get(p)).find(
    (a) => a?.status === "connected"
  );

  const withIdToken = useCallback(
    async (
      action: (idToken: string) => Promise<void>,
      successMsg?: string,
      key = "global"
    ) => {
      if (!user) return;
      setBusyKey(key);
      try {
        await action(await user.getIdToken());
        if (successMsg) message.success(successMsg);
      } catch (error) {
        message.error(error instanceof Error ? error.message : "Operation failed");
      } finally {
        setBusyKey(null);
      }
    },
    [user, message]
  );

  const handleConnect = (provider: CloudProviderId) => {
    const doConnect = () =>
      withIdToken(
        async (idToken) => {
          window.location.href = await getOAuthStartUrl(provider, idToken);
        },
        undefined,
        `connect-${provider}`
      );
    // Switching provider (D11): old cloud keeps its files, everything selected
    // will be uploaded again to the new one
    if (config && config.provider !== provider && config.status !== "disabled") {
      modal.confirm({
        title: `Switch to ${PROVIDER_LABELS[provider]}?`,
        content:
          `Photos already uploaded to ${PROVIDER_LABELS[config.provider]} will stay there. ` +
          `Anything not yet on ${PROVIDER_LABELS[provider]} will be uploaded to it.`,
        okText: "Switch",
        onOk: doConnect,
      });
    } else {
      doConnect();
    }
  };

  const handleDisconnect = (provider: CloudProviderId) => {
    modal.confirm({
      title: `Disconnect ${PROVIDER_LABELS[provider]}?`,
      content:
        "Sync will be disabled. Photos already uploaded to your cloud stay there. " +
        "You can reconnect at any time.",
      okText: "Disconnect",
      okButtonProps: { danger: true },
      onOk: () =>
        withIdToken(
          async (idToken) => {
            if (config && config.status !== "disabled") await disableSync(idToken);
            await disconnectAccount(provider, idToken);
          },
          "Account disconnected",
          "disconnect"
        ),
    });
  };

  const handleSaveSelection = () =>
    withIdToken(
      async (idToken) => {
        await updateSyncSelection(buildSelection(), idToken);
        setSelectionDirty(false);
      },
      "Selection saved — sync will process it shortly",
      "save"
    );

  // Periodic tick so the Uploading -> Checking decay (based on lastUploadAt
  // age) re-evaluates even without new Firestore snapshots
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);


  // Per-view synced counts (aggregation queries): refreshed when the sync
  // makes progress and every 30s — not second-by-second.
  // countsReady gates the page reveal: everything renders in ONE stable shot
  // instead of flashing in piece by piece as each data source arrives.
  const [viewSynced, setViewSynced] = useState<Record<string, number>>({});
  const [countsReady, setCountsReady] = useState(false);
  // Safety valve for the loading gate: never hold the skeleton longer than 4s
  // (slow networks, degenerate selection states)
  useEffect(() => {
    const t = window.setTimeout(() => setCountsReady(true), 4000);
    return () => window.clearTimeout(t);
  }, []);
  const syncedProgressTrigger = config?.counters?.syncedPhotos ?? 0;
  useEffect(() => {
    if (configLoading || projectsLoading) return;
    if (!config || config.status === "disabled" || projectsData.length === 0) {
      setCountsReady(true);
      return;
    }
    // Wait for the stored selection to be mirrored into the checkboxes first,
    // otherwise the first count round would run against an empty selection
    const selectionHasViews = Object.keys(config.selection ?? {}).length > 0;
    if (selectionHasViews && checkedViews.size === 0 && !selectionDirty) return;
    const provider = config.provider;
    let cancelled = false;
    const refresh = async () => {
      const selected: string[] = [];
      for (const { project, views } of projectsData) {
        for (const v of views) {
          if (checkedViews.has(viewKey(project.projectId, v.viewId))) selected.push(v.viewId);
        }
      }
      const entries: Record<string, number> = {};
      await Promise.all(
        selected.map(async (viewId) => {
          try {
            const snap = await getCountFromServer(
              query(
                collection(db, "photos"),
                where("viewId", "==", viewId),
                where(`cloudSync.${provider}.status`, "==", "synced")
              )
            );
            entries[viewId] = snap.data().count;
          } catch (error) {
            console.error(`synced count failed for view ${viewId}:`, error);
          }
        })
      );
      if (!cancelled) setViewSynced((prev) => ({ ...prev, ...entries }));
    };
    refresh().finally(() => setCountsReady(true));
    const id = window.setInterval(refresh, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configLoading,
    projectsLoading,
    config?.provider,
    config?.status,
    syncedProgressTrigger,
    projectsData,
    checkedViews,
  ]);

  // Single reveal: hold ONE skeleton until every primary data source is ready
  // (accounts, config, projects, first per-view counts) — no piecewise flashes
  const pageReady = !accountsLoading && !configLoading && !projectsLoading && countsReady;
  const counters = config?.counters;
  // Real uploads move lastUploadAt; pure verification walks don't. Recent
  // upload activity = show Uploading, otherwise the walk is just Checking.
  const lastUploadAt = config?.lastUploadAt?.toDate?.() ?? null;
  const uploadingActive =
    lastUploadAt !== null && Date.now() - lastUploadAt.getTime() < 120_000;
  // Free space of the ACTIVE provider's account (quota cache lives on the
  // cloudAccounts doc, one per connected provider)
  const activeQuota = config ? accounts.get(config.provider)?.quota : undefined;
  const quotaFree =
    activeQuota && activeQuota.totalBytes !== null
      ? Math.max(0, activeQuota.totalBytes - activeQuota.usedBytes)
      : null;

  /** Global sync state tag, shown next to the ACTIVE provider's name. */
  const syncStatusTag = () => {
    if (!config || config.status === "disabled" || !counters) return null;
    if (config.status === "paused") {
      return (
        <Tag
          color="orange"
          style={{ fontWeight: 600 }}
          icon={
            <MaterialIcon
              name="pause_circle"
              size={14}
              style={{ marginRight: 4, verticalAlign: "-2px" }}
            />
          }
        >
          {PAUSE_REASON_LABELS[config.pauseReason ?? ""] ?? "Paused"}
        </Tag>
      );
    }
    if (config.backfill?.status === "done" && counters.failedPhotos > 0) {
      return <Tag color="red">Upload failed</Tag>;
    }
    if (config.backfill?.status === "done") {
      return <Tag color="green">Up to date</Tag>;
    }
    return (
      <Tag
        color="processing"
        icon={
          <MaterialIcon
            name="progress_activity"
            size={13}
            className="dslr-sync-spin"
            style={{ marginRight: 4, verticalAlign: "-2px" }}
          />
        }
      >
        {uploadingActive ? "Uploading" : "Checking"}
      </Tag>
    );
  };

  return (
    <AppLayout>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <MaterialIcon name="cloud_sync" size={26} /> Cloud Sync
          </Title>
          <Text type="secondary">
            Automatically upload every photo (JPG + RAW) to your personal cloud as soon as it is
            received. Deleting photos on either side never deletes them on the other.
          </Text>
        </div>

        {!pageReady ? (
          <>
            <Card title={cardTitle("Connection")} size="small">
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </Card>
            <Card title={cardTitle("What to sync")} size="small">
              <Skeleton active paragraph={{ rows: 5 }} title={false} />
            </Card>
          </>
        ) : (
          <>
            {/* Connection */}
            <Card title={cardTitle("Connection")} size="small">
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {ENABLED_PROVIDERS.map((provider, providerIdx) => {
                  const account = accounts.get(provider);
                  const isConfigProvider = config?.provider === provider;
                  const isConnected = account?.status === "connected";
                  return (
                    <div key={provider}>
                      {providerIdx > 0 && <Divider style={{ margin: "0 0 12px" }} />}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <ProviderLogo
                          provider={provider}
                          size={18}
                          muted={
                            !isConnected ||
                            !(isConfigProvider && config && config.status !== "disabled")
                          }
                        />
                        <Text strong style={{ fontSize: 15 }}>
                          {PROVIDER_LABELS[provider]}
                        </Text>
                        {isConnected && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {account.email}
                          </Text>
                        )}
                        {isConfigProvider && syncStatusTag()}
                        {isConnected && account?.rootFolderUrl && (
                          <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={
                              <MaterialIcon
                                name="folder_open"
                                size={14}
                                style={{ marginRight: 4, verticalAlign: "-2px" }}
                              />
                            }
                            href={account.rootFolderUrl}
                            target="_blank"
                          >
                            Open folder
                          </Button>
                        )}
                        {account?.status === "revoked" && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            Access revoked
                          </Text>
                        )}
                        <div style={{ flex: 1 }} />
                        <Space wrap>
                          {isConnected &&
                            (!config || config.status === "disabled" || !isConfigProvider) &&
                            (() => {
                              // Sync already active on ANOTHER provider -> this
                              // is a switch, not a first enable: say so and
                              // confirm (D11) before proceeding
                              const isSwitch =
                                !!config && config.status !== "disabled" && !isConfigProvider;
                              const doEnable = () =>
                                withIdToken(
                                  async (idToken) => {
                                    await enableSync(provider, idToken);
                                  },
                                  isSwitch
                                    ? `Sync switched to ${PROVIDER_LABELS[provider]}`
                                    : "Sync enabled",
                                  `enable-${provider}`
                                );
                              return (
                                <Button
                                  type="primary"
                                  size="small"
                                  loading={busyKey === `enable-${provider}`}
                                  onClick={() =>
                                    isSwitch && config
                                      ? modal.confirm({
                                          title: `Switch sync to ${PROVIDER_LABELS[provider]}?`,
                                          content:
                                            `Photos already uploaded to ${PROVIDER_LABELS[config.provider]} will stay there. ` +
                                            `Anything not yet on ${PROVIDER_LABELS[provider]} will be uploaded to it.`,
                                          okText: "Switch",
                                          onOk: doEnable,
                                        })
                                      : doEnable()
                                  }
                                >
                                  {isSwitch
                                    ? `Switch sync to ${PROVIDER_LABELS[provider]}`
                                    : "Enable sync"}
                                </Button>
                              );
                            })()}
                          {isConnected && isConfigProvider && config?.status === "active" && (
                            <Button
                              size="small"
                              loading={busyKey === "pause"}
                              onClick={() => withIdToken(pauseSync, "Sync paused", "pause")}
                            >
                              Pause
                            </Button>
                          )}
                          {isConnected && isConfigProvider && config?.status === "paused" && (
                            <Button
                              type="primary"
                              size="small"
                              loading={busyKey === "resume"}
                              onClick={() => withIdToken(resumeSync, "Sync resumed", "resume")}
                            >
                              Resume
                            </Button>
                          )}
                          {isConnected && (
                            <Button size="small" danger onClick={() => handleDisconnect(provider)}>
                              Disconnect
                            </Button>
                          )}
                          {account?.status === "revoked" && (
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => handleConnect(provider)}
                            >
                              Reconnect
                            </Button>
                          )}
                          {!account && (
                            <Button
                              size="small"
                              type="primary"
                              loading={busyKey === `connect-${provider}`}
                              onClick={() => handleConnect(provider)}
                            >
                              Connect
                            </Button>
                          )}
                        </Space>
                      </div>
                      {isConnected && account.quota && (
                        <div style={{ marginLeft: 26, marginTop: 6 }}>
                          <MiniStorageBar quota={account.quota} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {!connectedAccount && (
                  <Text type="secondary">
                    Connect your cloud account to start syncing. MasterLapse only gets access to the
                    folders it creates.
                  </Text>
                )}
              </Space>
            </Card>

            {/* Selection — ShareDialog-style per-project view lists */}
            {config && config.status !== "disabled" && (
              <Card title={cardTitle("What to sync")} size="small">
                {projectsData.length === 0 ? (
                  <Empty description="No projects yet" />
                ) : (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {projectsData.map(({ project, views }) => {
                      const selectedCount = views.filter((v) =>
                        checkedViews.has(viewKey(project.projectId, v.viewId))
                      ).length;
                      const allProjectSelected =
                        views.length > 0 && selectedCount === views.length;
                      return (
                        <div key={project.projectId}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              justifyContent: "space-between",
                              marginBottom: 10,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                              <SectionLabel style={{ marginBottom: 0 }}>
                                {project.name}
                              </SectionLabel>
                              <Button
                                type="link"
                                size="small"
                                style={{ fontWeight: 400, fontSize: 14, padding: 0 }}
                                onClick={() =>
                                  toggleProject(project.projectId, views, !allProjectSelected)
                                }
                              >
                                {allProjectSelected ? "Deselect all" : "Select all"}
                              </Button>
                            </div>
                          </div>
                          <div
                            style={{
                              border: BORDER,
                              borderRadius: RADIUS,
                              overflow: "hidden",
                              background: "#fff",
                            }}
                          >
                            {views.map((view, idx) => {
                              const checked = checkedViews.has(
                                viewKey(project.projectId, view.viewId)
                              );
                              const total = view.stats?.photoCount ?? 0;
                              const synced = viewSynced[view.viewId];
                              const viewFolderUrl =
                                config?.folders?.[view.viewId]?.folderWebUrl ?? null;
                              return (
                                <div
                                  key={view.viewId}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "10px 14px",
                                    borderTop: idx === 0 ? "none" : "1px solid #f0f0f0",
                                  }}
                                >
                                  <Checkbox
                                    id={`sync-view-${view.viewId}`}
                                    checked={checked}
                                    onChange={() => toggleView(project.projectId, view.viewId)}
                                  />
                                  <div
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <label
                                      htmlFor={`sync-view-${view.viewId}`}
                                      style={{ minWidth: 0, cursor: "pointer" }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 500,
                                          color: checked ? "#262626" : SECONDARY,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {view.name}
                                      </div>
                                    </label>
                                    {viewFolderUrl && (
                                      <a
                                        href={viewFolderUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Open this view's folder in your cloud"
                                        style={{
                                          display: "inline-flex",
                                          color: "#1677ff",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <MaterialIcon name="folder_open" size={16} />
                                      </a>
                                    )}
                                  </div>
                                  {checked && synced !== undefined && synced >= total && (
                                    <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                                      Up to date
                                    </Tag>
                                  )}
                                  <Text style={{ fontSize: 12, color: SECONDARY }}>
                                    {checked && synced !== undefined
                                      ? `${Math.min(synced, total).toLocaleString()}/${total.toLocaleString()} photos · ${formatBytes(viewBytes(view))}`
                                      : `${total.toLocaleString()} photos · ${formatBytes(viewBytes(view))}`}
                                  </Text>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {quotaFree !== null && selectedTotals.bytes > quotaFree && (
                      <Alert
                        type="warning"
                        showIcon
                        message={`The selection (~${formatBytes(selectedTotals.bytes)}) may not fit in the free space on your cloud (${formatBytes(quotaFree)}). Sync will pause automatically when the cloud is full.`}
                      />
                    )}
                    {counters && counters.failedPhotos > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={`${counters.failedPhotos.toLocaleString()} photo${
                          counters.failedPhotos === 1 ? "" : "s"
                        } failed to upload — they will be retried automatically.`}
                      />
                    )}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Selecting a whole project also includes views created in it later. Any
                      missed photo is recovered automatically
                      {config.lastReconcileAt
                        ? ` — last check ${new Date(config.lastReconcileAt).toLocaleString()}`
                        : ""}
                      .
                    </Text>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <Text>
                        Selected: <Text strong>{selectedTotals.photos.toLocaleString()}</Text>{" "}
                        photos · Estimated upload: ~
                        <Text strong>{formatBytes(selectedTotals.bytes)}</Text>
                      </Text>
                      <Space size={12}>
                        <Button
                          type="text"
                          disabled={!selectionDirty || busyKey !== null}
                          onClick={() => setSelectionDirty(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          size="large"
                          className="btn-primary-emphasis"
                          disabled={!selectionDirty}
                          loading={busyKey === "save"}
                          onClick={handleSaveSelection}
                        >
                          Save selection
                        </Button>
                      </Space>
                    </div>
                  </Space>
                )}
              </Card>
            )}

          </>
        )}
      </Space>
    </AppLayout>
  );
}
